import React, { useState, useEffect, useRef } from "react";
import ChordProgressionPreQuiz from "./ChordProgressionPreQuiz";
import ChordProgressionQuiz, { ChordProgressionQuizOptions } from "./ChordProgressionQuiz";
import PlaybackOptionsPanel from "../../../components/PlaybackOptionsPanel";
import LearnMoreBanner from "../../../components/LearnMoreBanner";
import { useSoundLibrary } from "../../../hooks/useSoundLibrary";
import { useDrillLocalStorage } from "../../../hooks/useDrillLocalStorage";
import { usePlaybackSettings } from "../../../hooks/usePlaybackSettings";
import { Note } from "../../../soundLibrary";
import { ROUTES } from "../../../config/routes";
import { SEO_METADATA } from "../../../config/seoMetadata";
import SEO from "../../../components/SEO";
import styles from "../../../styles/ChordProgression.module.css";
import { buildChord, generateIncorrectProgressions, buildScale, ChordType } from "../../../utils/musicTheory";

const initialQuizOptions: ChordProgressionQuizOptions = {
    selectedChords: [1, 2, 4, 5, 6],
    progressionLength: 4,
    keyNote: "C4" as Note,
    mode: "questions",
    questionLimit: 6,
    timeLimitSeconds: 60,
    difficulty: 1,
};

export default function ChordProgressionWrapper() {
    const { playChord, playNote, stopAll, clearAbort } = useSoundLibrary();
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const isMountedRef = useRef(true);
    const abortPlaybackRef = useRef(false);

    // Persist quiz options to localStorage
    const [quizOptions, setQuizOptions] = useDrillLocalStorage<ChordProgressionQuizOptions>(
        "drill_progression_storage",
        initialQuizOptions
    );

    // Persist playback settings to localStorage (shared across all drills)
    const [playbackOptions, setPlaybackOptions] = usePlaybackSettings();

    const [quizStarted, setQuizStarted] = useState<boolean>(false);

    const [questionCount, setQuestionCount] = useState(0);
    const [attemptedQuestions, setAttemptedQuestions] = useState(0);
    const [score, setScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(quizOptions.timeLimitSeconds || 0);
    const [feedback, setFeedback] = useState<{ message: string; color: string } | null>(null);
    const [quizEnded, setQuizEnded] = useState(false);
    const [currentProgression, setCurrentProgression] = useState<{
        progressionNotes: Note[][];
        correctProgression: number[];
        questionOptions: number[][];
    } | null>(null);

    const handlePlayProgression = async () => {
        if (!currentProgression) return;
        
        abortPlaybackRef.current = false;
        clearAbort();
        let activeVolume = playbackOptions.muted ? 0 : playbackOptions.volume * 0.01;
        
        for (let i = 0; i < currentProgression.progressionNotes.length; i++) {
            const chord = currentProgression.progressionNotes[i];
            if (abortPlaybackRef.current || !isMountedRef.current) {
                return;
            }
            
            // Stop the previous chord before playing the next one
            if (i > 0) {
                stopAll();
            }
            
            await playChord(playbackOptions.instrument, chord, {
                velocity: activeVolume,
                duration: 0.7, // 700ms duration
            });
            
            if (abortPlaybackRef.current || !isMountedRef.current) {
                return;
            }
            
            await new Promise((res) => setTimeout(res, 800)); // 800ms spacing (~30% slower)
        }
    };

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            abortPlaybackRef.current = true;
            stopAll();
        };
    }, [stopAll]);

    const handleStart = () => {
        setQuizStarted(true);
        setQuestionCount(0);
        setAttemptedQuestions(0);
        setScore(0);
        setTimeLeft(quizOptions.timeLimitSeconds || 0);
        setFeedback(null);
        setCurrentProgression(null);
    };

    const endQuiz = () => {
        abortPlaybackRef.current = true; // Signal to stop progression playback
        stopAll(); // Stop any currently playing chords
        setQuizEnded(true);
        setFeedback(null);
        setCurrentProgression(null);
    };

    const generateQuestion = () => {
        if (!quizStarted || !quizOptions.selectedChords.length || quizEnded) return;

        const { selectedChords, progressionLength, keyNote, difficulty = 1 } = quizOptions;

        // Generate a random progression from the selected chords
        const correctProgression: number[] = [];
        for (let i = 0; i < progressionLength; i++) {
            const randomChordIndex = Math.floor(Math.random() * selectedChords.length);
            correctProgression.push(selectedChords[randomChordIndex]);
        }

        // Generate incorrect progressions based on difficulty
        const incorrectProgressions = generateIncorrectProgressions(
            correctProgression,
            selectedChords,
            4, // Always 4 incorrect options
            progressionLength,
            difficulty
        );

        // Create question options (shuffle them)
        const questionOptions = [correctProgression, ...incorrectProgressions].sort(
            () => Math.random() - 0.5
        );

        // Build the actual note progressions for playback
        // Major scale chord qualities: I-ii-iii-IV-V-vi-vii°
        const majorKeyQualities: ChordType[] = ["MAJOR", "MINOR", "MINOR", "MAJOR", "MAJOR", "MINOR", "DIMINISHED"];
        
        const scale = buildScale(keyNote, "MAJOR");
        const progressionNotes: Note[][] = correctProgression.map((degree) => {
            const chordQuality = majorKeyQualities[degree - 1];
            const chordRoot = scale[degree - 1];
            return buildChord(chordRoot, chordQuality);
        });

        setCurrentProgression({
            progressionNotes,
            correctProgression,
            questionOptions,
        });
    };

    const playReferenceNote = () => {
        const activeVolume = playbackOptions.muted ? 0 : playbackOptions.volume * 0.01;
        playNote(playbackOptions.instrument, quizOptions.keyNote, { velocity: activeVolume });
    };

    const resetQuiz = () => {
        setQuizStarted(false);
        setQuestionCount(0);
        setAttemptedQuestions(0);
        setScore(0);
        setFeedback(null);
        setQuizEnded(false);
        setCurrentProgression(null);
        // Note: quizOptions and playbackOptions are preserved from localStorage

        // Scroll to top when resetting quiz
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const disableStart = () => {
        if (!quizOptions || !quizOptions.selectedChords) return true;
        return quizOptions.selectedChords.length < 2;
    };

    useEffect(() => {
        if (quizEnded) {
            // Scroll to top of page when quiz ends
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [quizEnded]);

    useEffect(() => {
        if (quizStarted) {
            // Scroll to top of page when quiz starts
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [quizStarted]);

    useEffect(() => {
        if (quizOptions.mode === "time" && timeLeft > 0 && quizStarted) {
            const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
            return () => clearInterval(timer);
        }
    }, [quizOptions.mode, timeLeft, quizStarted]);

    useEffect(() => {
        if (quizStarted && !currentProgression && !quizEnded) {
            generateQuestion();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quizStarted, currentProgression, quizEnded]);

    useEffect(() => {
        if (currentProgression && quizStarted && !quizEnded) {
            const timer = setTimeout(() => {
                handlePlayProgression();
            }, 300);
            return () => clearTimeout(timer);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentProgression, quizStarted, quizEnded]);

    return (
        <>
            <SEO {...SEO_METADATA.DRILL_PROGRESSIONS} />
            <div ref={wrapperRef} className={styles.chordsDrillWrapper}>
            <div className={styles.chordsDrillHeader}>
                <div className={styles.titleSection}>Chord Progressions Drill</div>

                {!quizStarted && (
                    <div className={styles.headerTop}>
                        <button
                            onClick={handleStart}
                            disabled={disableStart()}
                            className={styles.startQuizButton}
                        >
                            Start Quiz
                        </button>
                    </div>
                )}

                {quizStarted && !quizEnded && (
                    <div className={styles.quizControlsPanel}>
                        {(quizOptions.mode !== "endless") && (
                            <div className={styles.quizStatus}>
                                {quizOptions.mode === "time" && timeLeft > 0 && <div className={styles.statusItem}>⏱️ {timeLeft}s</div>}
                                {quizOptions.mode === "questions" && quizOptions.questionLimit && 
                                 questionCount < quizOptions.questionLimit && (
                                    <div className={styles.statusItem}>📝 {questionCount + 1}/{quizOptions.questionLimit}</div>
                                )}
                            </div>
                        )}
                        <div className={styles.quizActions}>
                            {((quizOptions.mode === "questions" && quizOptions.questionLimit && questionCount < quizOptions.questionLimit) || 
                              (quizOptions.mode === "time" && timeLeft > 0) ||
                              (quizOptions.mode === "endless" && !quizEnded)) && (
                                <button className={styles.playButton} onClick={() => handlePlayProgression()}>
                                    Play
                                </button>
                            )}
                            <button onClick={endQuiz} className={styles.endQuizButton}>
                                End Quiz
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.quizAndPlaybackOpts}>
                {quizStarted ? (
                    <ChordProgressionQuiz
                        {...quizOptions}
                        playbackOptions={playbackOptions}
                        currentProgression={currentProgression}
                        setCurrentProgression={setCurrentProgression}
                        questionCount={questionCount}
                        setQuestionCount={setQuestionCount}
                        attemptedQuestions={attemptedQuestions}
                        setAttemptedQuestions={setAttemptedQuestions}
                        score={score}
                        setScore={setScore}
                        timeLeft={timeLeft}
                        setTimeLeft={setTimeLeft}
                        feedback={feedback}
                        setFeedback={setFeedback}
                        generateQuestion={generateQuestion}
                        quizEnded={quizEnded}
                        setQuizEnded={setQuizEnded}
                        resetQuiz={resetQuiz}
                        playReferenceNote={playReferenceNote}
                        stopAll={stopAll}
                        abortPlaybackRef={abortPlaybackRef}
                    />
                ) : (
                    <div className={styles.preQuizWithBanner}>
                        <LearnMoreBanner
                            to={ROUTES.LEARN.PROGRESSIONS}
                            variant="intro"
                            introTitle="What are chord progressions?"
                            introText={
                                <>
                                    A <strong>chord progression</strong> is a sequence of chords labeled
                                    with Roman numerals (I, IV, V) relative to a key—the same pattern
                                    sounds similar in any key. Each chord has a role: I is home base,
                                    V pulls back to I, and IV provides lift.
                                </>
                            }
                            examplesLabel="Common progressions you'll hear:"
                            examples={[
                                { primary: "I-IV-V-I", secondary: "Classic resolution" },
                                { primary: "I-V-vi-IV", secondary: "Pop/rock staple" },
                                { primary: "ii-V-I", secondary: "Jazz standard" },
                            ]}
                        />
                        <ChordProgressionPreQuiz setQuizOptions={setQuizOptions} initialOptions={quizOptions} />
                    </div>
                )}

                <PlaybackOptionsPanel
                    initialOptions={playbackOptions}
                    onChange={setPlaybackOptions}
                    forcePlayMode="harmonic"
                />
            </div>

            {!quizStarted && (
                <div className={styles.startQuizFooter}>
                    <button
                        onClick={handleStart}
                        disabled={disableStart()}
                        className={styles.startQuizButton}
                    >
                        Start Quiz
                    </button>
                </div>
            )}

            {quizStarted && !quizEnded && (
                <div className={styles.quizControlsFooter}>
                    {(quizOptions.mode !== "endless") && (
                        <div className={styles.quizStatus}>
                            {quizOptions.mode === "time" && timeLeft > 0 && <div className={styles.statusItem}>⏱️ {timeLeft}s</div>}
                            {quizOptions.mode === "questions" && quizOptions.questionLimit && 
                             questionCount < quizOptions.questionLimit && (
                                <div className={styles.statusItem}>📝 {questionCount + 1}/{quizOptions.questionLimit}</div>
                            )}
                        </div>
                    )}
                    <div className={styles.quizActions}>
                        {((quizOptions.mode === "questions" && quizOptions.questionLimit && questionCount < quizOptions.questionLimit) || 
                          (quizOptions.mode === "time" && timeLeft > 0) ||
                          (quizOptions.mode === "endless" && !quizEnded)) && (
                            <button className={styles.playButton} onClick={() => handlePlayProgression()}>
                                Play
                            </button>
                        )}
                        <button onClick={endQuiz} className={styles.endQuizButton}>
                            End Quiz
                        </button>
                    </div>
                </div>
            )}
        </div>
        </>
    );
}
